/**
 * Generates a PIPEDA-scoped privacy policy for a client site from a small config.
 *
 * Every MTRX-built site ships the cookieless pageview beacon, which sends data to
 * our servers — so every site needs to say so. Rather than hand-writing six near
 * identical policies that drift apart, each site declares what it actually does
 * and the shared wording is generated from that.
 *
 * IMPORTANT: only pass flags that are TRUE for the site. A policy that claims a
 * contact form the site does not have is as wrong as one that omits the beacon.
 *
 * Block shapes match `PrivacyPolicy.jsx`:
 *   { t: "p",  text }                     paragraph
 *   { t: "h",  text }                     subheading
 *   { t: "ul", items: [text] }            bullet list
 *   { t: "table", head: [...], rows: [[cell]] }
 *
 * `text` / `cell` is a string, or an array of strings and { href, label } links.
 *
 * @typedef {object} PrivacyConfig
 * @property {string}  businessName   Trading name, e.g. "SuperB Barber Shop".
 * @property {string}  domain         Public domain, no scheme, e.g. "superbbarber.ca".
 * @property {string}  contactEmail   Where privacy requests go.
 * @property {string}  [postalAddress] Business mailing address, if published.
 * @property {string}  [effective]    Display date the policy took effect.
 * @property {string}  [updated]      Display date of the last substantive change.
 * @property {false|{ fields: string[], autoReply?: boolean }} [contactForm]
 *   The form's real fields, if the site has one.
 * @property {boolean} [cms]          Site pulls published content from the MTRX CMS.
 * @property {boolean} [healthPractice] Site belongs to a regulated health practitioner.
 *   Adds an explicit statement that the WEBSITE collects no health information —
 *   worth saying plainly, because a clinic's site is exactly where a visitor will
 *   assume otherwise. Records kept inside the practice are governed by the
 *   practitioner's own obligations (in Ontario, PHIPA), not by a website policy.
 * @property {boolean} [analytics]     Site ships the MTRX cookieless pageview beacon.
 *   Defaults to true because most of our sites do — set it FALSE for a site with no
 *   `@mtrx/cms-client` dependency, or the policy will describe collection that
 *   never happens.
 * @property {false|{ name: string, href: string }} [booking]
 *   Third-party booking provider the site links or embeds.
 * @property {Array<{ name: string, purpose: string, href?: string }>} [embeds]
 *   Third-party embeds that load in the visitor's browser (maps, players, feeds).
 * @property {Array<[string, string, string]>} [extraProviders]
 *   Extra sub-processor rows: [provider, what it handles, where].
 */

const MTRX = "MTRX Studio";

/** @param {PrivacyConfig} config */
export function buildPrivacyPolicy(config) {
	const {
		businessName,
		domain,
		contactEmail,
		postalAddress,
		contactForm = false,
		cms = false,
		analytics = true,
		healthPractice = false,
		booking = false,
		embeds = [],
		extraProviders = [],
	} = config;

	const email = { href: `mailto:${contactEmail}`, label: contactEmail };
	const sections = [];

	// ---------------------------------------------------------------- overview
	sections.push({
		id: "overview",
		title: "Overview",
		blocks: [
			{
				t: "p",
				text: `${businessName} collects as little personal information as possible through this website. We do not sell it, we do not trade it, and we do not use hidden trackers to follow you around the web.`,
			},
			{
				t: "p",
				text: "This policy explains what this website collects, why, who it is shared with, and what you can ask us to do about it. It is written to meet our obligations under Canada's Personal Information Protection and Electronic Documents Act (PIPEDA).",
			},
			{
				t: "p",
				text: [
					"Questions, access requests, and complaints go to ",
					email,
					".",
				],
			},
		],
	});

	// -------------------------------------------------------------- who we are
	sections.push({
		id: "who-we-are",
		title: "Who we are",
		blocks: [
			{
				t: "p",
				text: `${businessName} operates this website at ${domain} and is the organization accountable for the personal information described here.`,
			},
			{
				t: "p",
				text: `The site was built and is maintained for us by ${MTRX}, a web design studio in Toronto, Ontario. They act as our service provider, handle personal information only on our instructions, and are named below wherever that is the case.`,
			},
			...(postalAddress ? [{ t: "p", text: postalAddress }] : []),
		],
	});

	// -------------------------------------------------------------- collection
	const collection = [];

	if (contactForm) {
		collection.push(
			{ t: "h", text: "Information you give us" },
			{
				t: "p",
				text: "When you submit the form on this website, we collect what you type into it:",
			},
			{ t: "ul", items: contactForm.fields },
			{
				t: "p",
				text: contactForm.autoReply
					? "We use this only to reply to you and follow up about your enquiry, and to send you an automatic confirmation that we received it. There are no hidden fields, and we do not buy or append data about you from other sources."
					: "We use this only to reply to you and follow up about your enquiry. There are no hidden fields, and we do not buy or append data about you from other sources.",
			}
		);
	}

	if (analytics) {
		collection.push(
			{ t: "h", text: "Anonymous website measurement" },
			{
				t: "p",
				text: `This site uses a private measurement system run by ${MTRX} on our behalf, rather than a third-party analytics service, because it keeps the data minimal. When you load a page it records only:`,
			},
			{
				t: "ul",
				items: [
					"Which site was visited",
					"The page path you viewed (for example, /about)",
					"The domain that referred you (for example, google.com) — never the full referring URL",
					"Your two-letter country code, derived from your network connection",
				],
			},
			{
				t: "p",
				text: "This uses no cookies. It does not store your IP address, does not create a device fingerprint or visitor ID, and cannot link page views to you or follow you across other websites. The result is a count of visits, not a profile.",
			}
		);
	}

	collection.push(
		{ t: "h", text: "Server logs" },
		{
			t: "p",
			text: "This site is delivered by Vercel, which keeps short-lived server logs for reliability, abuse prevention, and security. Those logs can include your IP address, browser and device type, the pages requested, and timestamps. They are not used for marketing or analytics.",
		}
	);

	if (healthPractice) {
		collection.push(
			{ t: "h", text: "Health information" },
			{
				t: "p",
				text: "This website does not collect health information. There is no intake form, no booking system, and no patient portal on it. If you email or call us, whatever you choose to tell us is handled as part of our professional records, under the obligations that apply to a regulated health practitioner — not under this website policy. Please do not send health details through any website.",
			}
		);
	}

	sections.push({
		id: "what-we-collect",
		title: "Information we collect",
		blocks: collection,
	});

	// ----------------------------------------------------------------- cookies
	const cookieRows = analytics
		? [["Website measurement", "Counts visits so we know which pages are useful", "No"]]
		: [];
	if (booking) {
		cookieRows.push([
			`${booking.name} booking`,
			"Handles appointment booking when you choose to book",
			"Yes — set by " + booking.name,
		]);
	}
	for (const embed of embeds) {
		cookieRows.push([embed.name, embed.purpose, "Yes — set by " + embed.name]);
	}

	sections.push({
		id: "cookies",
		title: "Cookies and similar technologies",
		blocks: [
			{
				t: "p",
				text: "A cookie is a small file a website stores in your browser. This site sets none of its own, and none are required for it to work.",
			},
			...(cookieRows.length
				? [{ t: "table", head: ["Technology", "Purpose", "Sets cookies?"], rows: cookieRows }]
				: []),
			{
				t: "p",
				text:
					cookieRows.length > 1
						? "The third-party features above are operated by their own companies under their own privacy policies. You can block or delete cookies in your browser settings; doing so may stop those features from working, but the rest of the site will be unaffected."
						: "You can block or delete cookies through your browser settings without losing access to anything on this site.",
			},
		],
	});

	// ------------------------------------------------------------------ how we use
	const uses = [
		...(analytics
			? ["Understand which pages and referral sources bring people to this site"]
			: []),
		"Keep the site secure, available, and free of spam",
		"Meet our legal, tax, and record-keeping obligations",
	];
	if (contactForm) {
		uses.unshift(
			"Respond to your enquiry and follow up about it",
			"Provide the services you asked us about"
		);
	}
	if (booking) uses.splice(1, 0, "Manage appointments you book with us");

	sections.push({
		id: "how-we-use",
		title: "How we use your information",
		blocks: [
			{ t: "p", text: "We use the information described above to:" },
			{ t: "ul", items: uses },
			{
				t: "p",
				text: "We will not use your information for a new, unrelated purpose without asking you first. We do not sell, rent, or trade personal information, and we do not make automated decisions about you that have legal or similarly significant effects.",
			},
		],
	});

	// ----------------------------------------------------------------- consent
	sections.push({
		id: "consent",
		title: "Consent",
		blocks: [
			...(contactForm
				? [
						{
							t: "p",
							text: "When you fill out and submit the form on this website, you are expressly consenting to us using that information to reply to you.",
						},
				  ]
				: []),
			{
				t: "p",
				text: analytics
					? "For the anonymous page-view counting and security logging described above, we rely on implied consent — the standard basis for ordinary website operation under PIPEDA — together with the browser controls listed in the cookies section."
					: "For the security logging described above, we rely on implied consent — the standard basis for ordinary website operation under PIPEDA.",
			},
			{
				t: "p",
				text: "You can withdraw your consent at any time by emailing us, subject to legal restrictions and reasonable notice.",
			},
		],
	});

	// ----------------------------------------------------------------- sharing
	const providerRows = [
		["Vercel Inc.", "Website hosting, delivery, and server logs", "United States"],
		[
			MTRX,
			[
				"Builds and maintains this site",
				analytics ? "stores the anonymous page-view counts" : null,
				cms ? "hosts the content management system behind this site's text and images" : null,
			]
				.filter(Boolean)
				.join(", "),
			"Canada, with data stored in the United States",
		],
	];
	if (contactForm) {
		providerRows.push([
			"Resend",
			"Delivers your form submission to our inbox" +
				(contactForm.autoReply ? " and sends your confirmation email" : ""),
			"United States",
		]);
	}
	if (booking) {
		providerRows.push([booking.name, "Appointment booking", "United States and globally"]);
	}
	for (const embed of embeds) {
		providerRows.push([embed.name, embed.purpose, "United States and globally"]);
	}
	providerRows.push(["Google LLC", "Google Fonts, which loads the site's typefaces", "United States and globally"]);
	providerRows.push(...extraProviders);

	sections.push({
		id: "sharing",
		title: "Service providers and storage outside Canada",
		blocks: [
			{
				t: "p",
				text: "We do not sell your personal information, and we do not disclose it to third parties for their own marketing. We rely on a small number of service providers to run this site, and each receives only what it needs.",
			},
			{
				t: "table",
				head: ["Provider", "What it handles", "Where it is processed"],
				rows: providerRows,
			},
			{
				t: "p",
				text: "Because these providers operate outside Canada, your information may be stored or processed in the United States or other countries. While it is there, it is subject to the laws of that country and may be accessible to foreign courts, law enforcement, and government authorities under those laws.",
			},
			{
				t: "p",
				text: "We may also disclose personal information where we are legally required to — for example in response to a valid court order or subpoena — or where necessary to establish or defend a legal claim, or to protect someone's safety.",
			},
		],
	});

	// --------------------------------------------------------------- retention
	sections.push({
		id: "retention",
		title: "How long we keep information",
		blocks: [
			{
				t: "ul",
				items: [
					...(contactForm
						? [
								"Enquiries that do not become customers: kept for up to 24 months, then deleted.",
								"Customer records: kept for the length of our relationship, and afterward for as long as tax, accounting, and legal obligations require.",
						  ]
						: []),
					...(analytics
						? [
								"Anonymous page-view counts: retained as aggregate statistics. They contain no personal information, so they are not tied to any individual.",
						  ]
						: []),
					"Server logs: kept briefly by our hosting provider under its own retention schedule.",
				],
			},
		],
	});

	// ---------------------------------------------------------------- security
	sections.push({
		id: "security",
		title: "How we protect your information",
		blocks: [
			{
				t: "p",
				text: "This site is served over HTTPS, so traffic between your browser and it is encrypted. Access to any information collected here is limited to the people who need it, and our providers are chosen for their security practices.",
			},
			{
				t: "p",
				text: "No method of transmission or storage is perfectly secure, so we cannot guarantee absolute security. Please do not send sensitive personal information — health details, financial details, government identifiers — through a website form.",
			},
		],
	});

	// ------------------------------------------------------------------ rights
	sections.push({
		id: "your-rights",
		title: "Your privacy rights",
		blocks: [
			{ t: "p", text: "Under Canadian privacy law you can:" },
			{
				t: "ul",
				items: [
					"Ask what personal information we hold about you, and get a copy of it",
					"Ask us to correct it if it is wrong or incomplete",
					"Withdraw your consent to our use of it, subject to legal restrictions",
					"Ask us to delete it, where we are not required to keep it",
					"Complain about how we have handled it",
				],
			},
			{
				t: "p",
				text: [
					"Email ",
					email,
					" and we will respond within 30 days. We may need to verify your identity first. If you are not satisfied with our response, you can complain to the Office of the Privacy Commissioner of Canada at ",
					{ href: "https://www.priv.gc.ca", label: "priv.gc.ca" },
					".",
				],
			},
		],
	});

	// ------------------------------------------------------------- links/kids
	sections.push({
		id: "links",
		title: "Links to other websites",
		blocks: [
			{
				t: "p",
				text: "This site links to other websites and social media profiles. We do not control them and are not responsible for their privacy practices — read their policies before giving them your information.",
			},
		],
	});

	sections.push({
		id: "children",
		title: "Children's privacy",
		blocks: [
			{
				t: "p",
				text: "This website is intended for a general audience and is not directed at children. We do not knowingly collect personal information from children. If you believe a child has given us information, email us and we will delete it.",
			},
		],
	});

	sections.push({
		id: "changes",
		title: "Changes to this policy",
		blocks: [
			{
				t: "p",
				text: "We may update this policy as our practices or the law change. The “last updated” date at the top always reflects the current version, and material changes will be highlighted on this page.",
			},
		],
	});

	sections.push({
		id: "contact",
		title: "Contact us and complaints",
		blocks: [
			{
				t: "p",
				text: ["Questions, access requests, and complaints go to ", email, "."],
			},
			...(postalAddress ? [{ t: "p", text: postalAddress }] : []),
		],
	});

	return sections;
}
